"use client";

import type { FilterOption } from "@workspace/ui/components";
import {
  Button,
  ButtonSize,
  ButtonVariant,
  Progress,
  ProgressColor,
  Text,
  TextColor,
  TextSize,
  TextVariant,
  TextWeight,
} from "@workspace/ui/components";
import {
  Modal,
  ProductMiniature,
  SortingFiltering,
} from "@workspace/ui/widgets";
import React, { useCallback, useEffect, useRef, useState } from "react";

import { useCategory } from "@/context";
import { NewsletterForm } from "@/forms";
import { useCategoryParams, useCategorySearchLogic } from "@/hooks";
import { stripHtml } from "@/utils/stringModifiers";

// Skeleton components pozostają bez zmian...
const ProductsSkeleton = () => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {[...Array(6)].map((_, index) => (
        <div key={index} className="animate-pulse">
          <div className="bg-gray-200 h-[430px] rounded-lg mb-2" />
          <div className="bg-gray-200 h-4 rounded mb-1" />
          <div className="bg-gray-200 h-4 rounded w-3/4" />
        </div>
      ))}
    </div>
  );
};

const CategorySkeleton = () => {
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2 px-4">
        <div className="h-8 bg-gray-200 rounded-md w-3/4 animate-pulse" />
        <div className="space-y-2">
          <div className="h-4 bg-gray-200 rounded w-full animate-pulse" />
          <div className="h-4 bg-gray-200 rounded w-5/6 animate-pulse" />
          <div className="h-4 bg-gray-200 rounded w-4/6 animate-pulse" />
        </div>
      </div>

      <div className="flex flex-row px-4 gap-2">
        <div className="h-4 bg-gray-200 rounded w-20 animate-pulse" />
        <div className="h-4 bg-gray-200 rounded w-20 animate-pulse" />
        <div className="h-4 bg-gray-200 rounded w-20 animate-pulse" />
        <div className="h-4 bg-gray-200 rounded w-20 animate-pulse" />
        <div className="h-4 bg-gray-200 rounded w-32 animate-pulse" />
      </div>

      <div className="px-4">
        <ProductsSkeleton />
      </div>
    </div>
  );
};

export default function CategoryView({
  params,
}: {
  params: Promise<{ slug: string }>;
}) {
  const [resolvedSlug, setResolvedSlug] = useState<string | null>(null);
  const [isNewsletterModalOpen, setIsNewsletterModalOpen] =
    useState<boolean>(false);
  const initializationRef = useRef<boolean>(false);
  const effectBlockedRef = useRef<boolean>(false);
  const initialLoadDoneRef = useRef<boolean>(false);

  const { getCategoryData, state } = useCategory();

  // Użyj hooka dla parametrów kategorii
  const {
    slug,
    selectedMinPrice,
    selectedMaxPrice,
    showOnSaleOnly,
    selectedOption,
    selectedColorOptions,
    selectedSizeOptions,
    appliedColorOptions,
    appliedSizeOptions,
    appliedMinPrice,
    appliedMaxPrice,
    appliedOnSaleOnly,
    updateURL,
    parseFiltersFromURL,
    syncFiltersFromURL,
    setSelectedMinPrice,
    setSelectedMaxPrice,
    setShowOnSaleOnly,
    setSelectedOption,
    setSelectedColorOptions,
    setSelectedSizeOptions,
    setSelectedFeatureOptions,
    selectedFeatureOptions,
    appliedFeatureOptions,
  } = useCategoryParams();

  const {
    initialized,
    setInitialized,
    category,
    loadingCategory,
    loadingMore,
    hasMorePages,
    sortOrders,
    handleApplyFilters,
    handleLoadMore,
    initializeFromURL,
    performSearchWithCurrentFilters,
    filteredResults,
  } = useCategorySearchLogic({
    slug: slug ?? null,
    updateURL,
    syncFiltersFromURL,
  });

  // Get slug from params
  useEffect(() => {
    params.then((resolvedParams) => {
      setResolvedSlug(resolvedParams.slug);
    });
  }, [params]);

  // Stable callback for handling sorting changes
  const handleSortingChange = useCallback(async () => {
    if (
      effectBlockedRef.current ||
      !initialized ||
      !initialLoadDoneRef.current ||
      !resolvedSlug
    ) {
      console.log(
        "Effect blocked - initialization in progress, not initialized, or initial load not done",
      );
      return;
    }

    console.log("Sorting changed, reloading category data...");
    await performSearchWithCurrentFilters();
  }, [initialized, performSearchWithCurrentFilters, resolvedSlug]);

  // Initialization effect pozostaje bez zmian...
  useEffect(() => {
    if (!resolvedSlug || initializationRef.current) return;

    console.log("Starting category initialization...");
    initializationRef.current = true;
    effectBlockedRef.current = true;

    const initialize = async () => {
      try {
        console.log("Loading initial category data...");
        let result = null;
        try {
          result = await getCategoryData(resolvedSlug, 1, {
            colors: [],
            sizes: [],
            features: [],
            minPrice: null,
            maxPrice: null,
            onSaleOnly: false,
            sort: selectedOption,
          });
          console.log(
            "getCategoryData completed successfully, result:",
            result,
          );
          console.log("category state after getCategoryData:", category);
          console.log("state.category after getCategoryData:", state.category);
        } catch (error) {
          console.error("getCategoryData failed:", error);
        }

        // Użyj bezpośrednio result z getCategoryData zamiast czekać na aktualizację stanu
        const categoryData = result;

        console.log("Using direct result from getCategoryData:", {
          hasResult: !!result,
          hasFilterData: !!result?.filterData,
          colorsCount: result?.filterData?.colors?.length || 0,
          result: result,
        });

        if (!categoryData?.filterData) {
          initialLoadDoneRef.current = true;
          setTimeout(() => {
            effectBlockedRef.current = false;
            setInitialized(true);
          }, 100);
          return;
        }

        console.log("Category filter data loaded:", categoryData.filterData);
        console.log("Current URL search params:", window.location.search);

        const urlFilters = parseFiltersFromURL(categoryData.filterData);
        console.log("URL filters parsed:", urlFilters);

        await initializeFromURL(categoryData.filterData, urlFilters);

        const hasUrlFilters =
          urlFilters.colors.length > 0 ||
          urlFilters.sizes.length > 0 ||
          urlFilters.features.length > 0 ||
          urlFilters.minPrice !==
            Math.floor(Number(categoryData.filterData.minPrice || 0)) ||
          urlFilters.maxPrice !==
            Math.floor(Number(categoryData.filterData.maxPrice || 0)) ||
          urlFilters.onSaleOnly ||
          urlFilters.sort !== "default";

        if (hasUrlFilters) {
          console.log("Performing initial category search with URL filters...");

          if (
            urlFilters.colors.length > 0 &&
            urlFilters.minPrice === 0 &&
            urlFilters.maxPrice === 0
          ) {
            console.log(
              "Skipping search - prices not properly initialized yet",
            );
            initialLoadDoneRef.current = true;
            setTimeout(() => {
              effectBlockedRef.current = false;
              setInitialized(true);
            }, 100);
            return;
          }

          const searchFilters = {
            colors: urlFilters.colors.map((c: FilterOption) => c.id),
            sizes: urlFilters.sizes.map((s: FilterOption) => s.id),
            features: urlFilters.features.map((f: FilterOption) => f.id),
            minPrice:
              urlFilters.minPrice !==
              Math.floor(Number(categoryData.filterData.minPrice || 0))
                ? urlFilters.minPrice
                : null,
            maxPrice:
              urlFilters.maxPrice !==
              Math.floor(Number(categoryData.filterData.maxPrice || 0))
                ? urlFilters.maxPrice
                : null,
            onSaleOnly: urlFilters.onSaleOnly,
            sort: urlFilters.sort,
          };

          console.log("Search filters being sent:", searchFilters);
          await getCategoryData(resolvedSlug, 1, searchFilters);
        }

        initialLoadDoneRef.current = true;

        setTimeout(() => {
          effectBlockedRef.current = false;
          setInitialized(true);
        }, 100);
      } catch (error) {
        console.error("Category initialization error:", error);
        initialLoadDoneRef.current = true;
        setTimeout(() => {
          effectBlockedRef.current = false;
          setInitialized(true);
        }, 100);
      }
    };

    initialize();
  }, [
    resolvedSlug,
    selectedOption,
    parseFiltersFromURL,
    initializeFromURL,
    getCategoryData,
    category,
    setInitialized,
  ]);

  // Effect for sorting changes
  useEffect(() => {
    handleSortingChange();
  }, [selectedOption]);

  // Render skeleton during loading
  if (loadingCategory) {
    return <CategorySkeleton />;
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2 px-4">
        <Text
          text={category?.name}
          color={TextColor.BLACK}
          weight={TextWeight.BOLD}
          variant={TextVariant.H1}
          fontSize={TextSize.XXL}
        />
        <Text
          text={stripHtml(category?.description ?? "")}
          color={TextColor.GRAYSCALE_500}
          fontSize={TextSize.S}
        />
      </div>

      <div className="flex flex-col px-4">
        {category && category.filterData && sortOrders && (
          <SortingFiltering
            minPrice={Math.floor(Number(category.filterData.minPrice))}
            maxPrice={Math.floor(Number(category.filterData.maxPrice))}
            showOnSaleOnly={showOnSaleOnly}
            setShowOnSaleOnly={setShowOnSaleOnly}
            selectedMinPrice={
              selectedMinPrice ??
              Math.floor(Number(category.filterData.minPrice))
            }
            selectedMaxPrice={
              selectedMaxPrice ??
              Math.floor(Number(category.filterData.maxPrice))
            }
            setSelectedMinPrice={setSelectedMinPrice}
            setSelectedMaxPrice={setSelectedMaxPrice}
            sortOptions={sortOrders}
            selectedOption={selectedOption}
            setSelectedOption={setSelectedOption}
            filterColorOptions={category.filterData.colors}
            filterSizeOptions={category.filterData.sizes}
            selectedColorOptions={selectedColorOptions}
            selectedSizeOptions={selectedSizeOptions}
            setSelectedColorOptions={(options) =>
              setSelectedColorOptions(options as FilterOption[])
            }
            setSelectedSizeOptions={(options) =>
              setSelectedSizeOptions(options as FilterOption[])
            }
            appliedColorOptions={appliedColorOptions}
            appliedSizeOptions={appliedSizeOptions}
            appliedMinPrice={appliedMinPrice}
            appliedMaxPrice={appliedMaxPrice}
            appliedOnSaleOnly={appliedOnSaleOnly}
            onApplyFilters={handleApplyFilters}
            filterFeatureOptions={category.filterData.features}
            setSelectedFeatureOptions={(options) =>
              setSelectedFeatureOptions(options as FilterOption[])
            }
            selectedFeatureOptions={selectedFeatureOptions}
            appliedFeatureOptions={appliedFeatureOptions}
          />
        )}
      </div>

      <div className="flex flex-col gap-2 px-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredResults?.map((product) => (
            <ProductMiniature
              key={`${product.id}-${product.idProductAttribute}`}
              product={product}
            />
          ))}
        </div>

        {/* Loading more indicator */}
        {loadingMore && (
          <div className="flex justify-center items-center py-8">
            <div className="flex flex-col items-center gap-4">
              <Progress color={ProgressColor.GRAYSCALE_400} />
              <Text
                text="Ładowanie kolejnych produktów..."
                color={TextColor.GRAYSCALE_500}
                fontSize={TextSize.S}
              />
            </div>
          </div>
        )}

        {/* Load more button */}
        {hasMorePages && !loadingMore && (
          <div className="flex justify-center items-center my-8">
            <Button
              text="Ładuj więcej produktów"
              onClick={handleLoadMore}
              variant={ButtonVariant.PRIMARY}
              size={ButtonSize.L}
              style={{
                padding: "10px 25px",
              }}
            />
          </div>
        )}

        {/* End of products message */}
        {!hasMorePages && filteredResults && filteredResults.length > 12 && (
          <div className="text-center py-8">
            <Text
              text={`Wyświetlono wszystkie ${filteredResults.length} produktów w tej kategorii`}
              color={TextColor.GRAYSCALE_400}
              fontSize={TextSize.S}
            />
          </div>
        )}

        {/* No products message */}
        {!loadingCategory &&
          filteredResults &&
          filteredResults.length === 0 && (
            <div className="text-center py-12">
              <Text
                text="Brak produktów w tej kategorii"
                color={TextColor.GRAYSCALE_400}
                fontSize={TextSize.M}
              />
            </div>
          )}
      </div>

      {/* Newsletter form in the modal */}
      <Modal
        open={isNewsletterModalOpen}
        onClose={() => setIsNewsletterModalOpen(false)}
        fullSpaceModal
      >
        <NewsletterForm modalVersion />
      </Modal>
    </div>
  );
}
